// SEOPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Card } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/seoxs.PNG'
const SEOPage = () => {
    useEffect(() => {
        window.scroll(0, 0)
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const seoStats = [
        { number: '94%', label: 'of online experiences begin with search' },
        { number: '70%', label: 'of clicks go to organic results' },
        { number: '53%', label: 'of all website traffic comes from organic search' },
        { number: '5x', label: 'higher conversion rates from SEO leads' }
    ];

    const seoServices = [
        {
            title: "AI SEO",
            description: "Leverage artificial intelligence for advanced SEO strategy and implementation",
            icon: "fas fa-brain",
            path: "/services/seo/ai-seo"
        },
        {
            title: "Technical SEO",
            description: "Optimize website infrastructure for search engine crawlers",
            icon: "fas fa-cogs",
            path: "/services/seo/technical"
        },
        {
            title: "Ecommerce SEO",
            description: "Specialized SEO strategies for online stores",
            icon: "fas fa-shopping-cart",
            path: "/services/seo/ecommerce"
        },
        {
            title: "International SEO",
            description: "Expand your reach to global markets",
            icon: "fas fa-globe",
            path: "/services/seo/international"
        },
        {
            title: "GEO SEO",
            description: "Generative Engine Optimisation for AI-driven search",
            icon: "fas fa-globe-americas",
            path: "/services/seo/geo"
        }
    ];

    const seoQuestions = [
        'How can you drive sustainable organic traffic to your website?',
        'What SEO strategies will deliver the highest ROI for your business?',
        'How do you compare to competitors in search visibility?',
        'Which technical issues are preventing your content from ranking?',
        'How can you future-proof your SEO for AI-driven search?'
    ];

    const seoBenefits = [
        ['Sustainable Traffic Growth', 'Attract qualified visitors who actively search for your products or services'],
        ['Higher Conversion Rates', 'SEO leads have a 14.6% close rate compared to 1.7% for outbound methods'],
        ['Cost-Effective Marketing', 'Long-term ROI that reduces reliance on paid advertising over time'],
        ['Brand Authority & Trust', 'Ranking highly builds credibility and trust with your target audience'],
        ['Competitive Advantage', 'Outperform competitors in search results and capture market share']
    ];

    return (
        <div className="seo sv-page">
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>

            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">SEO</span> Services
                    </h1>
                    <p>Drive organic traffic, improve search rankings, and achieve measurable business results</p>
                </div>
            </div>

            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    A comprehensive <span className="sv-keyword-highlight">SEO strategy</span> is essential for driving sustainable organic traffic, improving search rankings, and achieving measurable business results in today's competitive digital landscape.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>SEO services</strong> combine technical expertise, content strategy, and data-driven insights to help your business dominate search results. We don't just focus on rankings — we focus on delivering qualified traffic that converts into customers.
                                </p>
                                <p className="sv-description">
                                    From technical SEO foundations to advanced AI optimization, our comprehensive approach ensures your brand is visible, trusted, and recommended across all search platforms — traditional and AI-driven alike.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start SEO Strategy
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">SEO Matters</span> for Your Business
                        </h2>
                        <Row className="sv-stats-grid">
                            {seoStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is SEO?</h2>
                                <p className="sv-section-description">
                                    <strong>Search Engine Optimization (SEO)</strong> is the practice of improving your website's visibility in search engine results pages. It involves optimizing your content, technical infrastructure, and authority signals to rank higher for relevant searches and attract qualified organic traffic.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Questions Our SEO Services Answer:</h4>
                                    {seoQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Invest in SEO?</h2>
                                <p className="sv-section-description">
                                    SEO is one of the most cost-effective and sustainable marketing channels, delivering long-term results that compound over time. Unlike paid advertising, organic search continues to drive traffic and leads without ongoing ad spend.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Sustainable Growth</h5>
                                        <p>Build a long-term asset that continues to drive traffic and leads over time</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-dollar-sign"></i>
                                        <h5>Cost-Effective</h5>
                                        <p>Higher ROI than paid advertising with compounding returns</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-users"></i>
                                        <h5>Qualified Traffic</h5>
                                        <p>Attract users actively searching for your products or services</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-trophy"></i>
                                        <h5>Competitive Edge</h5>
                                        <p>Outperform competitors and capture market share in search results</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our SEO Services</h2>
                                <p className="sv-section-description">
                                    Reachtix offers a comprehensive suite of SEO services designed to address every aspect of your organic search presence — from technical foundations to advanced AI optimization.
                                </p>

                                <Row className="sv-services-grid">
                                    {seoServices.map((service, index) => (
                                        <Col lg={4} md={6} key={index} className="mb-4">
                                            <Card className="p-4 h-100">
                                                <div className="sv-approach-icon">
                                                    <i className={service.icon}></i>
                                                </div>
                                                <div className="sv-approach-content">
                                                    <h4>{service.title}</h4>
                                                    <p className="service-description">{service.description}</p>
                                                    <Button
                                                        tag={Link}
                                                        to={service.path}
                                                        className="mt-3"
                                                        color='dark'
                                                    >
                                                        Learn More
                                                    </Button>
                                                </div>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Key Benefits of SEO With Reachtix</h2>
                                <div className="table-responsive">
                                    <table className="table sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Core Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {seoBenefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Transform Your Organic Performance?</h2>
                                <p className="sv-cta-description">
                                    Whether you're starting from scratch or looking to optimize an existing strategy, our comprehensive SEO services provide the expertise and results you need to dominate search results.
                                </p>
                                <p className="sv-cta-description">
                                    Let's build a search presence that drives sustainable growth and delivers measurable ROI.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-calendar-check mr-2"></i>
                                        Start SEO Consultation
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-clipboard-list mr-2"></i>
                                        Get SEO Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default SEOPage;